import { create } from 'zustand';

import type { ToastItem, ToastOptions, ToastVariant } from './toast-types';

const MAX_STACK = 3;
const DURATION_DEFAULT = 4000;
const DURATION_ERROR = 6000;

interface ToastState {
  toasts: ToastItem[];
  push: (variant: ToastVariant, message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

function nextId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t_${String(Date.now())}_${Math.random().toString(36).slice(2, 9)}`;
}

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (variant, message, options) => {
    const id = nextId();
    const duration =
      options?.duration ?? (variant === 'error' ? DURATION_ERROR : DURATION_DEFAULT);
    const item: ToastItem = {
      id,
      variant,
      message,
      duration,
      action: options?.action,
      createdAt: Date.now(),
    };
    set((s) => {
      const next = [...s.toasts, item];
      if (next.length > MAX_STACK) next.shift();
      return { toasts: next };
    });
    return id;
  },
  dismiss: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  clear: () => { set({ toasts: [] }); },
}));

interface ToastApi {
  success: (message: string, options?: ToastOptions) => string;
  error: (message: string, options?: ToastOptions) => string;
  warning: (message: string, options?: ToastOptions) => string;
  info: (message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
}

export function useToast(): ToastApi {
  const push = useToastStore((s) => s.push);
  const dismiss = useToastStore((s) => s.dismiss);
  return {
    success: (m, o) => push('success', m, o),
    error: (m, o) => push('error', m, o),
    warning: (m, o) => push('warning', m, o),
    info: (m, o) => push('info', m, o),
    dismiss,
  };
}

export function useToastList(): ToastItem[] {
  return useToastStore((s) => s.toasts);
}

export function useToastDismiss(): (id: string) => void {
  return useToastStore((s) => s.dismiss);
}
